import { useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { ThreeBand } from "../components/macaron/ThreeBand";
import { BrandPortfolio } from "../components/macaron/BrandPortfolio";
import "../services.css";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (d) => ({ opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: d } }),
};

const ROW1_IMG = `${process.env.PUBLIC_URL}/img/practice-brand.jpg`;

const ROW2_IMG =
  "https://customer-assets-4nw71qhi.emergentagent.net/job_macaron-ai-launch/artifacts/ic3bai3x_aitech.png";

const ROW3_IMG = `${process.env.PUBLIC_URL}/img/practice-vm.jpg`;

const Eyebrow = ({ children, center }) => (
  <div className={`mac-eyebrow-row ${center ? "center" : ""}`}>
    <ThreeBand />
    <p className="mac-eyebrow">{children}</p>
  </div>
);

const capabilities = [
  ["01", "Brand Strategy & Services"],
  ["02", "Product Design & Industrial Design"],
  ["03", "Exhibition & Experiential Design"],
  ["04", "Digital Product Design (UX/UI)"],
  ["05", "Design Operations (DesignOps)"],
];

const shelf = [
  { c: "var(--pink)", w: 30, h: 44 },
  { c: "var(--iris)", w: 34, h: 52 },
  { c: "var(--cyan)", w: 28, h: 40 },
  { c: "var(--pink)", w: 32, h: 48 },
  { c: "var(--purple)", w: 36, h: 56 },
  { c: "var(--cyan)", w: 30, h: 42 },
];

const logos = ["Jo Malone", "MAC Cosmetics", "Prada", "Valentino", "Uniqlo", "Sephora", "Bloomingdale's", "Bogg Bag", "Charlotte Tilbury", "American Museum of Natural History"]; // eslint-disable-line no-unused-vars

export default function ServicesPage() {
  const { openContact } = useOutletContext();

  return (
    <div className="svc-page" data-testid="services-page">
      {/* SECTION 1 - HERO */}
      <section className="svc-hero" data-testid="services-hero">
        <div className="svc-container" style={{ position: "relative", zIndex: 1 }}>
          <motion.div variants={fadeUp} custom={0} initial="hidden" animate="show">
            <Eyebrow>SERVICES</Eyebrow>
          </motion.div>
          <motion.h1 variants={fadeUp} custom={0} initial="hidden" animate="show" className="svc-tagline" data-testid="bravery-word" style={{ marginTop: 24, whiteSpace: "normal" }}>
            <span className="mac-gradient-text">"Bravery"</span> is the word we live by. If you can dream it, we can build it.
          </motion.h1>
          <motion.p variants={fadeUp} custom={0.4} initial="hidden" animate="show" className="svc-closer" style={{ marginTop: 18 }}>
            We help brands create what's needed today, and what's next.
          </motion.p>
        </div>
      </section>

      {/* SECTION 2 - CAPABILITIES MENU */}
      <section className="svc-section svc-cap-section" data-testid="capabilities-section">
        <div className="svc-container">
          <div className="svc-cap-head">
            <div>
              <Eyebrow>THE CAPABILITIES MENU</Eyebrow>
              <h2 className="mac-h2" style={{ marginTop: 20 }}>Our expertise.<br />End to end.</h2>
            </div>
            <p className="svc-cap-intro">
              Specialists across every discipline we touch. Pick what you need, or hand us the whole brief.
            </p>
          </div>

          <div className="svc-cap-grid">
            {capabilities.map(([num, name], i) => (
              <div className="svc-cap-card" key={i} data-testid={`capability-card-${i + 1}`}>
                <span className="svc-cap-num">{num}</span>
                <span className="svc-cap-name">{name}</span>
                <ThreeBand />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 - OUR PRACTICES */}
      <section className="svc-section" data-testid="practices-section">
        <div className="svc-container">
          <Eyebrow>OUR PRACTICES</Eyebrow>
          <h2 className="mac-h2" style={{ marginTop: 20, maxWidth: 880, fontSize: "clamp(36px, 5vw, 58px)" }}>
            Every engagement is shaped by what the brand actually needs.
          </h2>
          <p className="mac-body" style={{ marginTop: 18, fontSize: 16 }}>
            The three practices below cover where we spend most of our time.
          </p>

          {/* ROW 01 */}
          <div className="svc-practice-row" data-testid="practice-row-1" style={{ borderTop: "none" }}>
            <div>
              <div className="svc-visual svc-vis-img" data-testid="practice-1-visual">
                <img src={ROW1_IMG} alt="Brand and product design studio" className="svc-vis-photo" />
              </div>
            </div>
            <div>
              <span className="svc-plabel">01 · PRACTICE</span>
              <h3 className="svc-ph" style={{ color: "var(--pink)" }}>Brand Identity &amp; Art Direction</h3>
              <p className="svc-pbody">
                We shape how brands <strong>feel</strong> and how they <strong>connect.</strong> End-to-end design from
                first concept to last. Identity systems, premium packaging, store layouts, and the creative vision
                that anchors a category.
              </p>
              <div className="svc-sublist">
                {[
                  "Brand identity, logos, photography & art direction",
                  "Premium packaging design",
                  "Store layout & spatial design",
                  "Conceptual design & creative visioning",
                  "Competitive Analysis & Industry Trend Reports",
                ].map((s, i) => (
                  <div className="svc-subrow" key={i}>{s}</div>
                ))}
              </div>
            </div>
          </div>

          {/* ROW 02 */}
          <div className="svc-practice-row" data-testid="practice-row-2">
            <div>
              <div className="svc-visual svc-vis-img" data-testid="practice-2-visual">
                <img src={ROW2_IMG} alt="AI and technology consulting roadmap" className="svc-vis-photo" />
              </div>
            </div>
            <div>
              <span className="svc-plabel" style={{ color: "var(--iris)" }}>02 · PRACTICE</span>
              <h3 className="svc-ph" style={{ color: "var(--iris)" }}>AI &amp; Technology Consulting</h3>
              <p className="svc-pbody">
                We help you figure out what technology to use, what AI to integrate, and which brand partnerships make
                sense. <strong>You bring the vision. We bring the roadmap.</strong>
              </p>
              <div className="svc-sublist">
                {[
                  "Technology strategy for in-store, pop-up & event experiences",
                  "AI integration consulting for brand workflows",
                  "Brand & technology partnership matching",
                  "Strategic technology roadmaps",
                ].map((s, i) => (
                  <div className="svc-subrow" key={i}>{s}</div>
                ))}
              </div>
            </div>
          </div>

          {/* ROW 03 */}
          <div className="svc-practice-row" data-testid="practice-row-3">
            <div>
              <div className="svc-visual svc-vis-img" data-testid="practice-3-visual">
                <img src={ROW3_IMG} alt="Visual merchandising and pop-up retail experience" className="svc-vis-photo" />
              </div>
            </div>
            <div>
              <span className="svc-plabel" style={{ color: "var(--cyan)" }}>03 · PRACTICE</span>
              <h3 className="svc-ph" style={{ color: "var(--cyan)" }}>Visual Merchandising, Pop-Ups &amp; Event Activations</h3>
              <p className="svc-pbody">
                We bridge physical spaces with <strong>unforgettable storytelling.</strong> Full-scale experiential
                environments, pop-ups, and spatial wayfinding designed to make customers feel something they'll never
                forget, and want to experience again and again.
              </p>
              <div className="svc-sublist">
                {[
                  "Concept design & creative direction",
                  "Vendor sourcing, selection & negotiation",
                  "Production oversight, quality control & build management",
                  "End-to-end installation & on-site project management",
                ].map((s, i) => (
                  <div className="svc-subrow" key={i}>{s}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3.5 - BRAND PORTFOLIO (own section) */}
      <section className="svc-section" data-testid="portfolio-section">
        <div className="svc-container">
          <Eyebrow>BRANDS WE HAVE PROUDLY WORKED WITH</Eyebrow>
          <div style={{ marginTop: 36 }}>
            <BrandPortfolio />
          </div>
        </div>
      </section>

      {/* SECTION 4 - VALUE PROP + LOGO WALL */}
      <section className="svc-model" data-testid="model-section">
        <div className="svc-model-glow" />
        <div className="svc-container">
          <Eyebrow center>THE MODEL</Eyebrow>
          <h2 className="svc-model-h">
            Specialist expertise,<br /><span className="mac-gradient-text">on demand.</span>
          </h2>
          <p className="svc-model-body">
            Dedicated, high-tier team of specialists tailored to your project. No agency bloat. No junior staffing.
            Just the right operators, assembled for your brief.
          </p>

          <div style={{ display: "flex", justifyContent: "center" }}>
            <button className="mac-btn svc-cta-lg" data-testid="strategy-call-btn" onClick={openContact}>
              Book a Complimentary Strategy Call <ArrowRight size={16} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
